import React, { useState, useMemo, useRef, useEffect } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Label,
} from "recharts";
import { Download, Calendar, Scale, DollarSign, TrendingUp, TrendingDown, Menu, Search, ChevronDown, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { SubscriptionService } from "../../services/subscriptionService";
import Dropdown from "./dropdown";

const FinancialGrowthCard = () => {
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [planFilter, setPlanFilter] = useState("All Plans");
    const [statusFilter, setStatusFilter] = useState("All Statuses");
    const [timeframe, setTimeframe] = useState(null);

    const [metrics, setMetrics] = useState(null);
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);

    const timeframeOptions = [
        { value: "30d", label: "Last 30 Days" },
        { value: "90d", label: "Last 90 Days" },
        { value: "6m", label: "Last 6 Months" },
        { value: "12m", label: "Last 12 Months" },
    ];

    const fetchMetrics = async (selectedTimeframe) => {
        try {
            setLoading(true);
            const [metricsData, tableData] = await Promise.all([
                SubscriptionService.getFinancialMetrics(selectedTimeframe),
                SubscriptionService.getSubscriptionTable()
            ]);
            setMetrics(metricsData);
            setSubscriptions(tableData);
            if (!timeframe && metricsData.timeframe) {
                setTimeframe(metricsData.timeframe);
            }
        } catch (error) {
            console.error("Error fetching financial data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMetrics(timeframe);
    }, [timeframe]);

    // Dynamic filtering logic
    const filteredData = useMemo(() => {
        return subscriptions.filter(item => {
            const matchesSearch = item.restaurantName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.email.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesPlan = planFilter === "All Plans" || item.planType === planFilter;
            const matchesStatus = statusFilter === "All Statuses" || item.status === statusFilter;
            return matchesSearch && matchesPlan && matchesStatus;
        });
    }, [searchQuery, planFilter, statusFilter, subscriptions]);

    // Paginated data
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    const paginatedData = filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    const handleClearFilters = () => {
        setSearchQuery("");
        setPlanFilter("All Plans");
        setStatusFilter("All Statuses");
        setCurrentPage(1);
    };

    const plans = ["All Plans", ...new Set(subscriptions.map(item => item.planType))];
    const statuses = ["All Statuses", ...new Set(subscriptions.map(item => item.status))];

    const [isPlanOpen, setIsPlanOpen] = useState(false);
    const [isStatusOpen, setIsStatusOpen] = useState(false);

    const planRef = useRef(null);
    const statusRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (planRef.current && !planRef.current.contains(event.target)) setIsPlanOpen(false);
            if (statusRef.current && !statusRef.current.contains(event.target)) setIsStatusOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const getPlanBadge = (plan) => {
        const styles = {
            "Multi-location": "bg-rose-50 text-rose-700 border-rose-100",
            "Single location": "bg-amber-50 text-amber-700 border-amber-100",
            "Free": "bg-orange-50 text-orange-700 border-orange-100",
        };
        return (
            <span className={`px-3 py-1 rounded-full text-[9px] font-black  tracking-tight border ${styles[plan] || "bg-gray-50 text-gray-400"}`}>
                {plan}
            </span>
        );
    };

    const getStatusBadge = (status) => {
        const styles = {
            "Active": "bg-emerald-50 text-emerald-600",
            "Trial": "bg-amber-50 text-amber-600",
            "Cancelled": "bg-rose-50 text-rose-500",
            "Inactive": "bg-gray-50 text-gray-400",
        };
        return (
            <span className={`px-3 py-1 rounded-lg text-[10px] font-black ${styles[status] || "bg-gray-50 text-gray-400"}`}>
                {status}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="bg-white rounded-[24px] p-12 text-center shadow-sm border border-gray-100 min-h-[400px] flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-200 border-t-red-500"></div>
            </div>
        );
    }

    const { cards, mrrTrend, subscriptionBreakdown, insights } = metrics || {};

    return (
        <div className="bg-white rounded-[24px] lg:rounded-[36px] p-6 sm:p-8 lg:p-10 shadow-sm border border-gray-100 font-sans mb-12">
            {/* Header & Metric Cards */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
                <h2 className="text-2xl font-black text-[#1F2937] tracking-tighter">
                    Financial & Growth Metrics
                </h2>
                <div className="flex items-center gap-3">
                    <Dropdown
                        label="Last 30 Days"
                        options={timeframeOptions}
                        selectedOption={timeframe}
                        onSelect={(opt) => setTimeframe(opt.value)}
                        buttonClassName="flex items-center gap-2 bg-gray-50 text-gray-500 px-5 py-2.5 rounded-xl text-xs font-black border border-gray-100 hover:bg-gray-100 transition-all"
                        icon={<Calendar size={14} />}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                {/* Card 1: LTV/CAC */}
                <div className="bg-[#DC2626] rounded-2xl p-6 text-white shadow-sm relative overflow-hidden group">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                            <Scale size={20} />
                        </div>
                        <div>
                            <p className="text-[10px] font-black  tracking-widest opacity-80 uppercase">LTV:CAC Ratio</p>
                            <p className="text-2xl font-black leading-none mt-1">{cards?.ltvCacRatio}</p>
                        </div>
                    </div>
                    <div className="flex justify-between items-center text-[10px] font-black  tracking-widest pt-4 border-t border-white/10 mt-2">
                        <span className="opacity-80">Lifetime Value</span>
                        <span className="opacity-80">Growth: 14%</span>
                    </div>
                </div>

                {/* Card 2: Avg Revenue */}
                <div className="bg-[#B47B00] rounded-2xl p-6 text-white shadow-sm relative overflow-hidden group">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                            <DollarSign size={20} />
                        </div>
                        <div>
                            <p className="text-[10px] font-black  tracking-widest opacity-80 uppercase">Avg. Revenue per Restaurant</p>
                            <p className="text-2xl font-black leading-none mt-1">${cards?.avgRevenuePerRestaurant}</p>
                        </div>
                    </div>
                    <div className="text-[10px] text-start font-black  tracking-widest pt-4 border-t border-white/10 opacity-80 mt-2">
                        Monthly Recurring
                    </div>
                </div>

                {/* Card 3: New Subscriptions */}
                <div className="bg-[#16A34A] rounded-2xl p-6 text-white shadow-sm relative overflow-hidden group">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                            <TrendingUp size={20} />
                        </div>
                        <div>
                            <p className="text-[10px] font-black  tracking-widest opacity-80 uppercase">New Subscriptions</p>
                            <p className="text-3xl font-black leading-none mt-1">{cards?.newSubscriptions}</p>
                        </div>
                    </div>
                    <div className="text-[10px] text-start font-black  tracking-widest pt-4 border-t border-white/10 opacity-80 mt-2">
                        Last 30 days
                    </div>
                </div>

                {/* Card 4: Cancellations */}
                <div className="bg-[#F97316] rounded-2xl p-6 text-white shadow-sm relative overflow-hidden group">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                            <TrendingDown size={20} />
                        </div>
                        <div>
                            <p className="text-[10px] font-black  tracking-widest opacity-80 uppercase">Cancellations</p>
                            <p className="text-3xl font-black leading-none mt-1">{cards?.cancellations}</p>
                        </div>
                    </div>
                    <div className="text-[10px] text-start font-black  tracking-widest pt-4 border-t border-white/10 opacity-80 mt-2">
                        Last 30 days
                    </div>
                </div>
            </div>

            {/* Chart & Insights Row */}
            <div className="flex flex-col xl:flex-row gap-10 mb-16">
                {/* MRR Trend Chart */}
                <div className="flex-1">
                    <div className="flex justify-between items-center mb-8">
                        <h3 className="text-base font-black text-[#1F2937] tracking-tight">Monthly Recurring Revenue Trend</h3>
                    </div>
                    <div className="h-[320px] w-full outline-none focus:outline-none">
                        <ResponsiveContainer width="100%" height="100%" className="outline-none focus:outline-none">
                            <LineChart data={mrrTrend} margin={{ left: 10, right: 10, top: 10, bottom: 10 }} accessibilityLayer={false} style={{ outline: 'none' }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                                <XAxis
                                    dataKey="month"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 10, fontWeight: 700, fill: '#9CA3AF' }}
                                    dy={10}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 10, fontWeight: 700, fill: '#9CA3AF' }}
                                    tickFormatter={(value) => `$${value >= 1000 ? (value / 1000).toFixed(0) + 'k' : value}`}
                                >
                                    <Label
                                        value="Revenue ($)"
                                        angle={-90}
                                        position="insideLeft"
                                        offset={-5}
                                        style={{ textAnchor: 'middle', fontSize: '10px', fontWeight: 800, fill: '#9CA3AF', textTransform: '' }}
                                    />
                                </YAxis>
                                <Tooltip
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                    formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="mrr"
                                    stroke="#881337"
                                    strokeWidth={3}
                                    dot={{ r: 4, fill: '#881337', strokeWidth: 2, stroke: '#FFFFFF' }}
                                    activeDot={{ r: 6, fill: '#881337', strokeWidth: 0 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Breakdown & Insights */}
                <div className="w-full xl:w-[380px] space-y-8">
                    {/* Subscription Breakdown */}
                    <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
                        <h3 className="text-[14px] font-black text-[#1F2937] tracking-tight mb-6">Subscription Breakdown</h3>
                        <div className="space-y-5">
                            {(subscriptionBreakdown || []).map((item, i) => {
                                const total = subscriptionBreakdown.reduce((acc, curr) => acc + curr.value, 0);
                                const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                                return (
                                    <div key={i} className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-2.5 h-2.5 rounded-full`} style={{ backgroundColor: item.color }}></div>
                                            <span className="text-[12px] font-bold text-gray-500">{item.name}</span>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-[12px] font-black text-[#1F2937] leading-none">{item.value}</p>
                                            <p className="text-[10px] font-bold text-gray-300 mt-1">{percentage}%</p>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    {/* Revenue Insights */}
                    <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
                        <h3 className="text-[14px] font-black text-[#1F2937] tracking-tight mb-6">Revenue Insights</h3>
                        <div className="space-y-6">
                            {[
                                { label: "MRR Growth Rate", value: insights?.mrrGrowthRate, valueColor: "text-emerald-500" },
                                { label: "Churn Rate", value: insights?.churnRate, valueColor: "text-rose-500" },
                                { label: "Customer LTV", value: insights?.customerLTV, valueColor: "text-[#1F2937]" },
                                { label: "CAC Payback", value: insights?.cacPayback, valueColor: "text-[#1F2937]" },
                            ].map((item, i) => (
                                <div key={i} className="flex items-center justify-between">
                                    <span className="text-[11px] font-bold text-gray-400  tracking-tight">{item.label}</span>
                                    <span className={`text-[13px] font-black ${item.valueColor}`}>{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Subscription Details Section */}
            <div className="pt-12 border-t border-gray-50">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8">
                    <h2 className="text-xl font-black text-[#1F2937] tracking-tighter">
                        Subscription Details
                    </h2>

                    <div className="flex items-center gap-3 self-end lg:self-center">
                        <div className="flex items-center gap-2 bg-white border border-gray-100 rounded-xl px-4 py-2 shadow-sm hover:bg-gray-50 transition-all">
                            <div className="flex flex-col gap-0.5 opacity-40">
                                <div className="w-3 h-0.5 bg-[#1F2937] rounded-full"></div>
                                <div className="w-2 h-0.5 bg-[#1F2937] rounded-full"></div>
                                <div className="w-3 h-0.5 bg-[#1F2937] rounded-full"></div>
                            </div>
                            <span className="text-[11px] font-bold text-gray-400">Show:</span>
                            <div className="relative flex items-center">
                                <select
                                    value={rowsPerPage}
                                    onChange={(e) => setRowsPerPage(Number(e.target.value))}
                                    className="appearance-none bg-transparent pr-5 text-[11px] font-black text-rose-800 focus:outline-none cursor-pointer leading-none"
                                >
                                    <option value={5}>5 rows</option>
                                    <option value={10}>10 rows</option>
                                </select>
                                <ChevronDown size={12} className="absolute right-0 text-rose-800 pointer-events-none" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters Toolbar */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <div className="relative group">
                        <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-[#881337] transition-colors" />
                        <input
                            type="text"
                            placeholder="Search restaurant..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="w-full bg-gray-50/50 border border-gray-100 rounded-xl py-2.5 pl-10 pr-4 text-[11px] font-bold focus:outline-none focus:ring-2 focus:ring-[#881337]/5 focus:border-[#881337] transition-all placeholder:text-gray-300 text-gray-600"
                        />
                    </div>

                    {/* Custom Plan Dropdown */}
                    <div className="relative" ref={planRef}>
                        <button
                            onClick={() => setIsPlanOpen(!isPlanOpen)}
                            className="w-full flex items-center justify-between bg-gray-50/50 border border-gray-100 rounded-xl py-2.5 px-4 text-[11px] font-extrabold text-[#1F2937] hover:bg-white hover:border-gray-200 transition-all focus:outline-none"
                        >
                            <div className="flex items-center gap-3">
                                <div className="flex gap-0.5">
                                    <div className="w-1.5 h-1.5 bg-gray-300 rounded-sm"></div>
                                    <div className="w-1.5 h-1.5 bg-gray-300 rounded-sm"></div>
                                </div>
                                <span>{planFilter === "All Plans" ? "Plan Types" : planFilter}</span>
                            </div>
                            <ChevronDown size={14} className={`text-gray-400 transition-transform ${isPlanOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {isPlanOpen && (
                            <div className="absolute top-full left-0 w-full mt-2 bg-white border border-gray-100 rounded-[18px] shadow-xl py-2 z-50 animate-in fade-in zoom-in duration-200">
                                {plans.map((plan) => (
                                    <button
                                        key={plan}
                                        onClick={() => {
                                            setPlanFilter(plan);
                                            setIsPlanOpen(false);
                                            setCurrentPage(1);
                                        }}
                                        className={`w-full text-left px-5 py-2.5 text-[11px] font-black transition-colors ${planFilter === plan
                                            ? 'bg-rose-50 text-[#881337]'
                                            : 'text-gray-500 hover:bg-gray-50 hover:text-[#111827]'
                                            }`}
                                    >
                                        {plan === "All Plans" ? "Show All Plans" : plan}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Custom Status Dropdown */}
                    <div className="relative" ref={statusRef}>
                        <button
                            onClick={() => setIsStatusOpen(!isStatusOpen)}
                            className="w-full flex items-center justify-between bg-gray-50/50 border border-gray-100 rounded-xl py-2.5 px-4 text-[11px] font-extrabold text-[#1F2937] hover:bg-white hover:border-gray-200 transition-all focus:outline-none"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-3.5 h-3.5 border-2 border-gray-300 rounded-full flex items-center justify-center">
                                    <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                                </div>
                                <span>{statusFilter === "All Statuses" ? "Statuses" : statusFilter}</span>
                            </div>
                            <ChevronDown size={14} className={`text-gray-400 transition-transform ${isStatusOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {isStatusOpen && (
                            <div className="absolute top-full left-0 w-full mt-2 bg-white border border-gray-100 rounded-[18px] shadow-xl py-2 z-50 animate-in fade-in zoom-in duration-200">
                                {statuses.map((status) => (
                                    <button
                                        key={status}
                                        onClick={() => {
                                            setStatusFilter(status);
                                            setIsStatusOpen(false);
                                            setCurrentPage(1);
                                        }}
                                        className={`w-full text-left px-5 py-2.5 text-[11px] font-black transition-colors ${statusFilter === status
                                            ? 'bg-rose-50 text-[#881337]'
                                            : 'text-gray-500 hover:bg-gray-50 hover:text-[#111827]'
                                            }`}
                                    >
                                        {status === "All Statuses" ? "Show All Statuses" : status}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <button
                        onClick={handleClearFilters}
                        className="flex items-center justify-center gap-2 bg-rose-50 text-rose-700 rounded-xl py-2.5 px-4 text-[11px] font-black  tracking-widest hover:bg-rose-100 transition-all border border-rose-100/50 shadow-sm"
                    >
                        <RotateCcw size={13} className="text-[#881337]" />
                        Clear Filters
                    </button>
                </div>

                {/* Table */}
                <div className="overflow-x-auto -mx-6 sm:-mx-8 lg:-mx-10">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="border-b border-gray-100">
                                <th className="px-10 py-4 text-left text-[11px] font-black text-gray-400 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        Restaurant Name
                                        <div className="flex flex-col gap-0.5 opacity-50">
                                            <ChevronDown size={8} className="rotate-180" />
                                            <ChevronDown size={8} />
                                        </div>
                                    </div>
                                </th>
                                <th className="px-6 py-4 text-center text-[11px] font-black text-gray-400 whitespace-nowrap">
                                    <div className="flex items-center justify-center gap-2">
                                        Plan Type
                                        <div className="flex flex-col gap-0.5 opacity-50">
                                            <ChevronDown size={8} className="rotate-180" />
                                            <ChevronDown size={8} />
                                        </div>
                                    </div>
                                </th>
                                <th className="px-6 py-4 text-center text-[11px] font-black text-gray-400 whitespace-nowrap">
                                    <div className="flex items-center justify-center gap-2">
                                        Monthly Revenue
                                        <div className="flex flex-col gap-0.5 opacity-50">
                                            <ChevronDown size={8} className="rotate-180" />
                                            <ChevronDown size={8} />
                                        </div>
                                    </div>
                                </th>
                                <th className="px-6 py-4 text-center text-[11px] font-black text-gray-400 whitespace-nowrap">
                                    <div className="flex items-center justify-center gap-2">
                                        Start Date
                                        <div className="flex flex-col gap-0.5 opacity-50">
                                            <ChevronDown size={8} className="rotate-180" />
                                            <ChevronDown size={8} />
                                        </div>
                                    </div>
                                </th>
                                <th className="px-10 py-4 text-center text-[11px] font-black text-gray-400 whitespace-nowrap">
                                    Status
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50/50">
                            {paginatedData.map((item, idx) => (
                                <tr key={idx} className="group hover:bg-gray-50/30 transition-colors">
                                    <td className="px-10 py-6 text-[13px] font-bold text-gray-500 text-left">
                                        {item.restaurantName}
                                    </td>
                                    <td className="px-6 py-6 text-center">
                                        {getPlanBadge(item.planType)}
                                    </td>
                                    <td className="px-6 py-6 text-[13px] font-extrabold text-[#1F2937] text-center">
                                        ${item.monthlyRevenue}
                                    </td>
                                    <td className="px-6 py-6 text-[13px] font-bold text-gray-400 text-center">
                                        {item.startDate ? new Date(item.startDate).toLocaleDateString() : 'N/A'}
                                    </td>
                                    <td className="px-10 py-6 text-center">
                                        {getStatusBadge(item.status)}
                                    </td>
                                </tr>
                            ))}
                            {paginatedData.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-10 py-12 text-center text-gray-400 font-bold text-sm">
                                        No restaurants found matching your criteria.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-gray-50 mt-4">
                    <p className="text-[11px] font-bold text-gray-400">
                        Showing <span className="text-[#1F2937] font-black">{filteredData.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0}</span> to <span className="text-[#1F2937] font-black">{Math.min(currentPage * rowsPerPage, filteredData.length)}</span> of <span className="text-[#1F2937] font-black">{filteredData.length}</span> restaurants
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className={`flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-100 rounded-xl text-[11px] font-black transition-all shadow-sm ${currentPage === 1 ? 'opacity-30 cursor-not-allowed text-gray-300' : 'text-gray-400 hover:bg-gray-50'}`}
                        >
                            <ChevronLeft size={14} />
                            Previous
                        </button>
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages || totalPages === 0}
                            className={`flex items-center gap-2 px-7 py-2.5 bg-white border border-gray-100 rounded-xl text-[11px] font-black transition-all shadow-[0_2px_10px_-4px_rgba(0,0,0,0.1)] ${currentPage === totalPages || totalPages === 0 ? 'opacity-30 cursor-not-allowed text-gray-300' : 'text-[#1F2937] hover:bg-gray-50'}`}
                        >
                            Next
                            <ChevronRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FinancialGrowthCard;
